import { SwapOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Modal, Space, Switch, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FC, useMemo, useState } from 'react';

import { isValidMobileNumber, UserWithStore } from './UserSelectionTable';

const invalidColumns: ColumnsType<UserWithStore> = [
  { title: '매장코드', dataIndex: 'storeCode', key: 'storeCode', width: 100 },
  { title: '매장명', dataIndex: 'storeName', key: 'storeName' },
  { title: '관리용 매장명', dataIndex: 'storeNickname', key: 'storeNickname' },
  {
    title: '연락처',
    dataIndex: 'userTel',
    key: 'userTel',
    render: (tel: string) => <Tag color='red'>{tel || '-'}</Tag>,
  },
  {
    title: '점주연락처',
    dataIndex: 'ownerTel',
    key: 'ownerTel',
    render: (tel: string) => (isValidMobileNumber(tel) ? <Tag color='green'>{tel}</Tag> : <span>{tel || '-'}</span>),
  },
  { title: '이메일', dataIndex: 'email', key: 'email' },
];

const useInvalidUserGroups = (users: UserWithStore[]) => {
  const invalidUsers = useMemo(() => users.filter((user) => !isValidMobileNumber(user.userTel ?? '')), [users]);
  const substitutableUsers = useMemo(
    () => invalidUsers.filter((user) => isValidMobileNumber(user.ownerTel)),
    [invalidUsers],
  );
  const unreachableUsers = useMemo(
    () => invalidUsers.filter((user) => !isValidMobileNumber(user.ownerTel)),
    [invalidUsers],
  );
  return { invalidUsers, substitutableUsers, unreachableUsers };
};

interface InvalidPhoneButtonProps {
  users: UserWithStore[];
}

export const InvalidPhoneButton: FC<InvalidPhoneButtonProps> = ({ users }) => {
  const [open, setOpen] = useState(false);
  const { invalidUsers, substitutableUsers } = useInvalidUserGroups(users);

  return (
    <>
      <Button
        type='primary'
        danger
        size='small'
        icon={<WarningOutlined />}
        disabled={invalidUsers.length === 0}
        onClick={() => setOpen(true)}
      >
        발송 불가 {invalidUsers.length}명
      </Button>

      <Modal
        title='알림톡 발송 불가 목록 (유효하지 않은 연락처)'
        open={open}
        onCancel={() => setOpen(false)}
        width={900}
        footer={null}
      >
        {substitutableUsers.length > 0 && (
          <Typography.Paragraph type='secondary'>
            점주 연락처가 유효한 경우(<Tag color='green'>초록색</Tag>) 대체 발송 기능을 활성화하면 자동으로 포함됩니다.
          </Typography.Paragraph>
        )}
        <Table<UserWithStore>
          rowKey='_id'
          columns={invalidColumns}
          dataSource={invalidUsers}
          pagination={{ pageSize: 10 }}
          size='small'
        />
      </Modal>
    </>
  );
};

interface FallbackPhoneControlProps {
  users: UserWithStore[];
  useOwnerTelFallback: boolean;
  onFallbackChange: (checked: boolean) => void;
}

export const FallbackPhoneControl: FC<FallbackPhoneControlProps> = ({
  users,
  useOwnerTelFallback,
  onFallbackChange,
}) => {
  const [open, setOpen] = useState(false);
  const { substitutableUsers, unreachableUsers } = useInvalidUserGroups(users);

  if (substitutableUsers.length === 0) return null;

  return (
    <>
      <Space size='small' align='center'>
        <Switch size='small' checked={useOwnerTelFallback} onChange={onFallbackChange} />
        <Typography.Text type={useOwnerTelFallback ? undefined : 'secondary'}>
          점주 연락처 대체 발송 ({substitutableUsers.length}명)
        </Typography.Text>
      </Space>
      {useOwnerTelFallback && (
        <Button type='link' size='small' icon={<SwapOutlined />} onClick={() => setOpen(true)}>
          목록 보기
        </Button>
      )}

      <Modal title='점주 연락처 대체 발송 목록' open={open} onCancel={() => setOpen(false)} width={900} footer={null}>
        <Typography.Paragraph>아래 {substitutableUsers.length}명은 점주 연락처로 대체 발송됩니다.</Typography.Paragraph>
        <Table<UserWithStore>
          rowKey='_id'
          columns={invalidColumns}
          dataSource={substitutableUsers}
          pagination={{ pageSize: 10 }}
          size='small'
        />
        {unreachableUsers.length > 0 && (
          <Typography.Paragraph type='secondary' style={{ marginTop: 16 }}>
            점주 연락처도 유효하지 않아 발송이 불가한 사용자: {unreachableUsers.length}명
          </Typography.Paragraph>
        )}
      </Modal>
    </>
  );
};
